import React, { useState, useRef, useEffect } from 'react';
import { FaArrowLeft, FaArrowRight, FaCheck, FaImage, FaTag, FaDollarSign, FaEye, FaMagic, FaChevronDown } from 'react-icons/fa';
import HelpTooltip from './HelpTooltip';
import { productTemplates, getTemplate } from '../data/productTemplates';

// Common product categories for marketplace
const COMMON_CATEGORIES = [
  'yoga',
  'meditation',
  'wellness',
  'fitness',
  'nutrition',
  'skincare',
  'aromatherapy',
  'herbal',
  'supplements',
  'accessories',
  'clothing',
  'books',
  'equipment',
  'general'
];

const ProductCreationWizard = ({ onClose, onSubmit, initialData = null, isEdit = false }) => {
  const [showTemplateSelection, setShowTemplateSelection] = useState(!initialData && !isEdit);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const categoryInputRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    description: initialData?.description || '',
    price: initialData?.price || '',
    discount: initialData?.discount || 0,
    category: initialData?.category || 'general',
    stock: initialData?.stock || 0,
    image: initialData?.image || '',
    images: initialData?.images || [],
    sku: initialData?.sku || '',
    shippingWeight: initialData?.shippingWeight || 0,
    shippingDimensions: initialData?.shippingDimensions || { length: 0, width: 0, height: 0 },
    shippingClass: initialData?.shippingClass || 'standard',
    returnPolicy: initialData?.returnPolicy || '30 days return',
    warranty: initialData?.warranty || '1 year manufacturer warranty',
    tags: initialData?.tags || [],
    gstRate: initialData?.gstRate !== undefined ? initialData.gstRate : 18,
    hsnCode: initialData?.hsnCode || ''
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target) &&
        categoryInputRef.current &&
        !categoryInputRef.current.contains(event.target)
      ) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const steps = [
    { number: 1, title: 'Basic Information', icon: <FaTag /> },
    { number: 2, title: 'Pricing & Inventory', icon: <FaDollarSign /> },
    { number: 3, title: 'Media & Details', icon: <FaImage /> },
    { number: 4, title: 'Review & Submit', icon: <FaEye /> }
  ];

  const handleTemplateSelect = (templateId) => {
    const template = getTemplate(templateId);
    if (template) {
      setSelectedTemplate(templateId);
      setFormData({
        ...formData,
        title: template.name,
        description: template.description,
        category: template.category,
        shippingWeight: template.shippingWeight,
        shippingDimensions: template.shippingDimensions,
        shippingClass: template.shippingClass,
        returnPolicy: template.returnPolicy,
        warranty: template.warranty,
        tags: template.tags || []
      });
      setShowTemplateSelection(false);
    }
  };

  const handleStartFromScratch = () => {
    setShowTemplateSelection(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    onSubmit(formData);
  };

  const calculateDiscountedPrice = () => {
    const price = parseFloat(formData.price) || 0;
    const discount = parseFloat(formData.discount) || 0;
    return price * (1 - discount / 100);
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.title && formData.description;
      case 2:
        return formData.price && formData.stock >= 0 && formData.hsnCode && formData.hsnCode.trim() !== '';
      case 3:
        return true; // Media is optional
      case 4:
        return formData.title && formData.price && formData.stock >= 0;
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product Title <span className="text-red-500">*</span>
                <HelpTooltip
                  title="Product Title"
                  content="Enter a clear, descriptive title for your product. This is what customers will see first."
                />
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., Premium Yoga Mat"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description <span className="text-red-500">*</span>
                <HelpTooltip
                  title="Product Description"
                  content="Provide detailed information about your product including features, benefits, and specifications."
                />
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={6}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="Describe your product in detail..."
                required
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price (₹) <span className="text-red-500">*</span>
                  <HelpTooltip
                    title="Product Price"
                    content="Enter the selling price of your product in Indian Rupees."
                  />
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Discount (%)
                  <HelpTooltip
                    title="Discount Percentage"
                    content="Enter a discount percentage (0-100). This will be applied to the original price."
                  />
                </label>
                <input
                  type="number"
                  name="discount"
                  value={formData.discount}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
                {formData.discount > 0 && formData.price && (
                  <p className="text-sm text-green-600 mt-1">
                    Discounted Price: ₹{calculateDiscountedPrice().toFixed(2)}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stock Quantity <span className="text-red-500">*</span>
                  <HelpTooltip
                    title="Stock Quantity"
                    content="Enter the number of units available for sale. Update this regularly to reflect your inventory."
                  />
                </label>
                <input
                  type="number"
                  name="stock"
                  value={formData.stock}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GST Rate (%) <span className="text-red-500">*</span>
                  <HelpTooltip
                    title="GST Rate (%)"
                    content="Enter the GST percentage for this product (e.g. 5, 12, 18, 28)."
                  />
                </label>
                <select
                  name="gstRate"
                  value={formData.gstRate}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required
                >
                  <option value={0}>0%</option>
                  <option value={5}>5%</option>
                  <option value={12}>12%</option>
                  <option value={18}>18%</option>
                  <option value={28}>28%</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                HSN Code <span className="text-red-500">*</span>
                <HelpTooltip
                  title="HSN Code"
                  content="Enter the HSN Code (Harmonized System of Nomenclature) for this product."
                />
              </label>
              <input
                type="text"
                name="hsnCode"
                value={formData.hsnCode}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="e.g. 9506"
                pattern="\d{4,8}"
                title="HSN Code must be a 4 to 8 digit number"
                minLength={4}
                maxLength={8}
                required
              />
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
                <HelpTooltip
                  title="Product Category"
                  content="Select a category from the dropdown or type your own. Categories help customers find your products in the marketplace."
                />
              </label>
              <div className="relative">
                <input
                  ref={categoryInputRef}
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  onFocus={() => setShowCategoryDropdown(true)}
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="Select or type a category"
                />
                <button
                  type="button"
                  onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Toggle category dropdown"
                >
                  <FaChevronDown className={`transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showCategoryDropdown && (
                  <div
                    ref={categoryDropdownRef}
                    className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                  >
                    {COMMON_CATEGORIES.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, category: cat });
                          setShowCategoryDropdown(false);
                          categoryInputRef.current?.focus();
                        }}
                        className={`w-full text-left px-4 py-2 hover:bg-purple-50 ${formData.category === cat ? 'bg-purple-100 font-semibold' : ''
                          }`}
                      >
                        <span className="capitalize">{cat}</span>
                      </button>
                    ))}
                    <div className="border-t border-gray-200 px-4 py-2 text-xs text-gray-500">
                      Tip: You can also type a custom category
                    </div>
                  </div>
                )}
              </div>
              {formData.category && (
                <p className="mt-1 text-xs text-gray-500">
                  Selected: <span className="font-medium capitalize">{formData.category}</span>
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SKU (Stock Keeping Unit)
                <HelpTooltip
                  title="SKU"
                  content="Optional: Enter a unique identifier for this product. If left empty, one will be auto-generated."
                />
              </label>
              <input
                type="text"
                name="sku"
                value={formData.sku}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="Auto-generated if not provided"
              />
            </div>
          </div >
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Main Image URL
                <HelpTooltip
                  title="Product Image"
                  content="Enter the URL of your product's main image. This will be the primary image shown to customers."
                />
              </label>
              <input
                type="url"
                name="image"
                value={formData.image}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="https://example.com/image.jpg"
              />
              {formData.image && (
                <div className="mt-2">
                  <img
                    src={formData.image}
                    alt="Preview"
                    className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Shipping Weight (kg)
                <HelpTooltip
                  title="Shipping Weight"
                  content="Enter the weight of the product in kilograms. This helps calculate shipping costs."
                />
              </label>
              <input
                type="number"
                name="shippingWeight"
                value={formData.shippingWeight}
                onChange={handleChange}
                min="0"
                step="0.1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Return Policy
                <HelpTooltip
                  title="Return Policy"
                  content="Specify your return policy (e.g., '30 days return', 'No returns'). This builds customer trust."
                />
              </label>
              <input
                type="text"
                name="returnPolicy"
                value={formData.returnPolicy}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., 30 days return"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Warranty
                <HelpTooltip
                  title="Warranty Information"
                  content="Enter warranty details (e.g., '1 year manufacturer warranty')."
                />
              </label>
              <input
                type="text"
                name="warranty"
                value={formData.warranty}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., 1 year manufacturer warranty"
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Product Preview</h3>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                {formData.image && (
                  <img
                    src={formData.image}
                    alt={formData.title}
                    className="w-full h-48 object-cover rounded-lg mb-4"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                <h4 className="text-xl font-bold mb-2">{formData.title || 'Product Title'}</h4>
                <p className="text-gray-600 mb-4">{formData.description || 'Product description...'}</p>
                <div className="flex items-center gap-4 mb-4">
                  {formData.discount > 0 ? (
                    <>
                      <span className="text-2xl font-bold text-purple-600">
                        ₹{calculateDiscountedPrice().toFixed(2)}
                      </span>
                      <span className="text-gray-400 line-through">
                        ₹{formData.price}
                      </span>
                      <span className="text-red-600 font-semibold">
                        -{formData.discount}% OFF
                      </span>
                    </>
                  ) : (
                    <span className="text-2xl font-bold text-purple-600">
                      ₹{formData.price || '0.00'}
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-600">
                  <p>Category: {formData.category || 'general'}</p>
                  <p>Stock: {formData.stock || 0} units</p>
                  {formData.sku && <p>SKU: {formData.sku}</p>}
                </div>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Your product will be submitted for admin approval. It will appear in the marketplace once approved.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Template Selection Screen
  if (showTemplateSelection) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <FaMagic className="text-purple-600" />
                Choose a Product Template
              </h2>
              <p className="text-gray-600 mt-1">Start with a template or create from scratch</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {Object.entries(productTemplates).map(([templateId, template]) => (
                <button
                  key={templateId}
                  onClick={() => handleTemplateSelect(templateId)}
                  className="p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all text-left"
                >
                  <h3 className="font-semibold text-lg mb-2">{template.name}</h3>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{template.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {template.tags?.slice(0, 3).map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="mt-3 text-xs text-purple-600 font-medium">
                    Use Template →
                  </div>
                </button>
              ))}
            </div>

            <div className="border-t border-gray-200 pt-6">
              <button
                onClick={handleStartFromScratch}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all font-medium text-gray-700"
              >
                Start from Scratch (No Template)
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {isEdit ? 'Edit Product' : 'Create New Product'}
              </h2>
              {selectedTemplate && (
                <p className="text-sm text-purple-600 mt-1">
                  Using template: {productTemplates[selectedTemplate]?.name}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${currentStep >= step.number ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-500'
                    }`}>
                    {currentStep > step.number ? (
                      <FaCheck />
                    ) : (
                      step.icon
                    )}
                  </div>
                  <span className={`text-xs mt-2 ${currentStep >= step.number ? 'text-purple-600 font-semibold' : 'text-gray-500'}`}>
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 ${currentStep > step.number ? 'bg-purple-600' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="p-6">
          {renderStepContent()}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 flex justify-between">
          <button
            onClick={currentStep === 1 ? onClose : handlePrevious}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <FaArrowLeft />
            {currentStep === 1 ? 'Cancel' : 'Previous'}
          </button>
          {currentStep < steps.length ? (
            <button
              onClick={handleNext}
              disabled={!isStepValid()}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              Next
              <FaArrowRight />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!isStepValid()}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <FaCheck />
              {isEdit ? 'Update Product' : 'Submit Product'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCreationWizard;

